import React, { useEffect, useContext, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { GlobalMenuContext } from "../context/GlobalMenuContext";
import { logoutUser } from "../Api";

function SidebarGroup({ group, collapseId, isActivePath }) {
  const groupHasActiveChild = group.children?.some(
    (child) => child.path && isActivePath(child.path),
  );

  const isHorizontal = document.documentElement.getAttribute("data-layout") === "horizontal";
  const shouldShow = groupHasActiveChild && !isHorizontal;

  return (
    <li className="nav-item">
      <a
        className={`nav-link menu-link ${groupHasActiveChild ? "active" : ""
          } ${shouldShow ? "" : "collapsed"
          }`}
        href={`#${collapseId}`}
        data-bs-toggle="collapse"
        role="button"
        aria-expanded={shouldShow}
        aria-controls={collapseId}
      >
        {group.icon_class && <i className={group.icon_class}></i>}
        <span>{group.label}</span>
      </a>

      <div
        className={`menu-dropdown collapse ${shouldShow ? "show" : ""
          }`}
        id={collapseId}
      >
        <ul className="nav nav-sm flex-column">
          {group.children?.map((item, childIndex) => (
            <li className="nav-item" key={item.id || childIndex}>
              <Link
                to={item.path || "#"}
                className={`nav-link ${isActivePath(item.path) ? "active" : ""
                  }`}
              >
                {item.icon_class && <i className={item.icon_class}></i>}
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function Sidebar({ hidden = false }) {
  const { username } = useUser();
  const location = useLocation();
  const { menus: rawMenus, loading } = useContext(GlobalMenuContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === "/dashboard" && rawMenus.length > 0) {
      localStorage.setItem("menus", JSON.stringify(rawMenus));
    }
  }, [location.pathname, rawMenus]);

  useEffect(() => {
    document.body.classList.remove("vertical-sidebar-enable");
    document.body.classList.remove("menu");
  }, [location.pathname]);

  const displayMenus = useMemo(() => {
    if (!rawMenus?.length) return [];

    const uniqueMenus = rawMenus.filter(
      (menu, index, self) =>
        index ===
        self.findIndex(
          (m) =>
            m.label.toLowerCase().trim() === menu.label.toLowerCase().trim() &&
            m.icon_class === menu.icon_class,
        ),
    );

    return uniqueMenus;
  }, [rawMenus]);

  const isActivePath = useCallback(
    (path) => {
      if (!path || path === "#") return false;

      const currentPath = location.pathname;

      // Exact match
      if (currentPath === path) return true;


      const pathParts = path.split("/").filter(Boolean);
      const locParts = currentPath.split("/").filter(Boolean);

      if (pathParts.length >= 2 && locParts.length >= 2) {
        return pathParts[0] === locParts[0] && pathParts[1] === locParts[1];
      } else if (pathParts.length > 0 && locParts.length > 0) {
        return (
          pathParts[0] === locParts[0] && pathParts.length === locParts.length
        );
      }

      return false;
    },
    [location.pathname],
  );

  const staticIds = [
    "sidebarDashboards",
    "sidebarProjects",
    "sidebarWorkforce",
    "sidebarProducts",
    "sidebarPurchase",
    "sidebarManage",
    "sidebarSales",
    "sidebarAccounts",
    "sidebarAccountsReport",
  ];

  const MAX_VISIBLE = 5;
  const visibleMenus = useMemo(
    () => displayMenus.slice(0, MAX_VISIBLE),
    [displayMenus],
  );
  const overflowMenus = useMemo(
    () => displayMenus.slice(MAX_VISIBLE),
    [displayMenus],
  );

  const moreHasActiveChild = useMemo(() => {
    return overflowMenus.some((group) =>
      group.children?.some((child) => child.path && isActivePath(child.path)),
    );
  }, [overflowMenus, isActivePath]);

  const isHorizontal = document.documentElement.getAttribute("data-layout") === "horizontal";
  const moreShouldShow = moreHasActiveChild && !isHorizontal;

  const handleLogout = async (e) => {
    e.preventDefault();
    await logoutUser();
    navigate("/", { state: { message: "You have been logged out successfully." } });
  };

  const MORE_ID = `sidebarMore_${location.pathname.replace(/\W/g, "_")}`;

  return (
    <div className={`app-menu navbar-menu ${hidden ? "d-none" : ""}`}>
      {/* Logo */}
      <div className="navbar-brand-box">
        <Link to="/dashboard" className="logo logo-dark">
          <span className="logo-sm">
            <img src="../assets/images/favicon.png" alt="" width={50} />
          </span>
          <span className="logo-lg">
            <img
              src="../assets/images/logo.png"
              alt="logo"
              className="mt-2"
              width={180}
            />
          </span>
        </Link>
      </div>

      {/* User dropdown */}
      <div className="dropdown sidebar-user m-1 rounded">
        <button
          type="button"
          className="btn material-shadow-none"
          data-bs-toggle="dropdown"
        >
          <span className="d-flex align-items-center gap-2">
            <img
              className="rounded header-profile-user"
              src="../../assets/images/users/avatar-1.jpg"
              alt="Avatar"
            />
            <span className="text-start">
              <span className="d-block fw-medium">{username || "Guest"}</span>
              <span className="d-block fs-14">
                <i className="ri-circle-fill fs-10 text-success align-baseline"></i>
                <span className="align-middle">Online</span>
              </span>
            </span>
          </span>
        </button>
        <div className="dropdown-menu dropdown-menu-end">
          <h6 className="dropdown-header">Welcome {username || "Admin"}!</h6>
          <Link className="dropdown-item" to="/profile">
            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
            Profile
          </Link>
          <div className="dropdown-divider"></div>
          <a className="dropdown-item" href="#!" onClick={handleLogout}>
            <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
            Logout
          </a>
        </div>
      </div>

      {/* Menus */}
      <div id="scrollbar">
        <div className="container-fluid">
          <div id="two-column-menu"></div>
          <ul className="navbar-nav" id="navbar-nav">
            <li className="menu-title">
              <span>Master Pages</span>
            </li>

            {loading ? (
              <li className="nav-item px-3 py-2 text-muted">
                Loading menus...
              </li>
            ) : displayMenus.length === 0 ? (
              <li className="nav-item px-3 py-2 text-danger">No menu access</li>
            ) : (
              <>
                {/* Render visible menus */}
                {visibleMenus.map((group, index) => {
                  const collapseId =
                    staticIds[index] || `sidebarGroup_${group.id || index}`;
                  return (
                    <SidebarGroup
                      key={group.id || index}
                      group={group}
                      collapseId={collapseId}
                      isActivePath={isActivePath}
                    />
                  );
                })}

                {/* Render "More" dropdown if overflow exists */}
                {overflowMenus.length > 0 && (
                  <li className="nav-item">
                    <a
                      className={`nav-link menu-link ${moreHasActiveChild ? "active" : ""
                        } ${moreShouldShow ? "" : "collapsed"
                        }`}
                      href={`#${MORE_ID}`}
                      data-bs-toggle="collapse"
                      role="button"
                      aria-expanded={moreShouldShow}
                      aria-controls={MORE_ID}
                    >
                      <i className="ri-briefcase-2-line"></i> More
                    </a>
                    <div
                      className={`collapse menu-dropdown ${moreShouldShow ? "show" : ""}`}
                      id={MORE_ID}
                    >
                      <ul className="nav nav-sm flex-column">
                        {overflowMenus.map((group, index) => {
                          const collapseId = `${MORE_ID}_${index}`;
                          return (
                            <SidebarGroup
                              key={group.id || index}
                              group={group}
                              collapseId={collapseId}
                              isActivePath={isActivePath}
                            />
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      </div>

      <div className="sidebar-background"></div>
    </div>
  );
}

export default Sidebar;
